import React, { useEffect, useRef, useState } from "react";
import api, { money, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Check, X, Lock, AlertTriangle, ShieldAlert, Package, FileWarning, Loader2, CheckCircle2, Upload, Receipt, PenLine } from "lucide-react";
import { toast } from "sonner";

const STAGE_COLORS = {
  "Assigned": "bg-slate-100 text-slate-700", "Accepted": "bg-blue-100 text-blue-700",
  "Ready to Start": "bg-cyan-100 text-cyan-700", "In Progress": "bg-indigo-100 text-indigo-700",
  "Change Order Needed": "bg-amber-100 text-amber-800", "Ready for Review": "bg-purple-100 text-purple-700",
  "Completed": "bg-emerald-100 text-emerald-700", "Payout Approved": "bg-teal-100 text-teal-700",
  "Paid": "bg-green-100 text-green-700",
};
const MAT_COLORS = { "Pending Approval": "bg-amber-100 text-amber-800", "Approved": "bg-blue-100 text-blue-700", "Denied": "bg-rose-100 text-rose-700", "Purchased": "bg-emerald-100 text-emerald-700" };
const CO_COLORS = { "Pending": "bg-amber-100 text-amber-800", "Approved": "bg-blue-100 text-blue-700", "Denied": "bg-rose-100 text-rose-700", "Completed": "bg-emerald-100 text-emerald-700" };

function ReqRow({ r }) {
  return (
    <li className="flex items-start gap-2 text-sm" data-testid={`req-${r.key}`}>
      {r.met ? <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> : <X className="w-4 h-4 text-rose-500 mt-0.5 shrink-0" />}
      <span className={r.met ? "text-slate-500" : "text-slate-800 font-medium"}>{r.label}{r.detail ? <span className="text-slate-400"> — {r.detail}</span> : null}</span>
    </li>
  );
}

export function JobCompliance({ wo, onChange, refreshKey }) {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ackRule, setAckRule] = useState(false);
  const [notes, setNotes] = useState("");

  const load = () => api.get(`/work-orders/${wo.id}/compliance`).then(({ data }) => { setC(data); setNotes(data.completion_notes || ""); }).catch(() => setC(false));
  useEffect(() => { load(); }, [wo.id, refreshKey]); // eslint-disable-line

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); load(); onChange?.(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.missing) toast.error(`${d.message} Missing: ${d.missing.join(", ")}`);
      else toast.error(typeof d === "string" ? d : "Action failed");
    }
    setBusy(false);
  };

  if (c === null) return <div className="bg-white border border-slate-200 rounded-xl p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>;
  if (!c) return null;

  const pending = c.contractor_response === "pending" || c.contractor_response == null;
  const accepted = c.contractor_response === "accepted";
  const started = ["In Progress", "Change Order Needed", "Ready for Review", "Completed", "Payout Approved", "Paid"].includes(c.stage);
  const submitted = ["Ready for Review", "Completed", "Payout Approved", "Paid"].includes(c.effective_stage || c.stage);

  return (
    <div className="space-y-4" data-testid="job-compliance">
      {/* Stage + payout */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-head font-semibold text-slate-900">Job Compliance</h3>
          <span data-testid="compliance-stage" className={`text-xs font-bold uppercase px-2 py-1 rounded ${STAGE_COLORS[c.effective_stage || c.stage] || "bg-slate-100 text-slate-700"}`}>{c.effective_stage || c.stage}</span>
        </div>
        <div className="mt-2 text-xs text-slate-500">Payout status: <span className="font-semibold text-slate-700" data-testid="payout-stage">{c.payout_stage}</span></div>
        {c.admin_review_note && !submitted && (
          <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="admin-review-note">
            <strong>SJ review note:</strong> {c.admin_review_note}
          </div>
        )}
      </div>

      {/* Communication rule — always visible */}
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-4" data-testid="communication-rule">
        <div className="flex items-center gap-2 text-rose-800 font-semibold text-sm"><ShieldAlert className="w-4 h-4" /> Customer Communication Rule</div>
        <p className="mt-1.5 text-sm text-rose-700 leading-relaxed">{c.communication_rule}</p>
      </div>

      {/* Accept / decline */}
      {pending && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid="accept-block">
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <Checkbox data-testid="ack-rule-checkbox" checked={ackRule} onCheckedChange={(v) => setAckRule(!!v)} className="mt-0.5" />
            <span>I acknowledge and agree to the Customer Communication Rule above.</span>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <Button data-testid="compliance-accept-btn" disabled={busy || !ackRule} className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => run(() => api.post(`/work-orders/${wo.id}/compliance/accept`, { acknowledge_rule: true }), "Job accepted")}><Check className="w-4 h-4 mr-1" /> Accept Job</Button>
            <Button data-testid="compliance-decline-btn" disabled={busy} variant="outline" className="text-rose-600 border-rose-200"
              onClick={() => run(() => api.post(`/work-orders/${wo.id}/compliance/decline`), "Job declined")}><X className="w-4 h-4 mr-1" /> Decline</Button>
          </div>
        </div>
      )}

      {/* Scope acknowledgement + Start gating */}
      {accepted && !started && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
          <h4 className="font-head font-semibold text-slate-900 text-sm">Before you can start</h4>
          {!c.scope_acknowledged && (
            <Button data-testid="ack-scope-btn" disabled={busy} variant="outline" className="w-full"
              onClick={() => run(() => api.post(`/work-orders/${wo.id}/compliance/acknowledge-scope`), "Scope confirmed")}>Confirm I reviewed the scope of work</Button>
          )}
          <ul className="space-y-1.5" data-testid="start-requirements">{c.start_requirements.map((r) => <ReqRow key={r.key} r={r} />)}</ul>
          <Button data-testid="start-job-btn" disabled={busy || !c.start_allowed} className="w-full bg-blue-600 hover:bg-blue-700"
            onClick={() => run(() => api.post(`/work-orders/${wo.id}/compliance/start`), "Job started")}>
            {c.start_allowed ? "Start Job" : <><Lock className="w-4 h-4 mr-1" /> Start Job (locked)</>}
          </Button>
        </div>
      )}

      {/* Active-job tools */}
      {started && !submitted && (
        <>
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-2">
            <h4 className="font-head font-semibold text-slate-900 text-sm">Job Actions</h4>
            <MaterialDialog wo={wo} threshold={c.material_threshold} onDone={load} />
            <ChangeOrderDialog wo={wo} warning={c.change_order_warning} onDone={load} />
            <IssueDialog wo={wo} types={c.issue_types} onDone={load} />
            {c.customer_signoff_required && <SignoffUpload wo={wo} signoff={c.customer_signoff} onDone={load} />}
          </div>

          {/* Submit completion gating */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
            <h4 className="font-head font-semibold text-slate-900 text-sm">Submit Job Complete</h4>
            <div>
              <Label className="text-xs">Completion notes</Label>
              <Textarea data-testid="completion-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-1"
                onBlur={() => { if (notes !== c.completion_notes) api.post(`/work-orders/${wo.id}/compliance/notes`, { completion_notes: notes }).then(load).catch(() => {}); }} placeholder="Describe the completed work…" />
            </div>
            <ul className="space-y-1.5" data-testid="completion-requirements">{c.completion_requirements.map((r) => <ReqRow key={r.key} r={r} />)}</ul>
            <Button data-testid="submit-complete-btn" disabled={busy || !c.completion_allowed}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              onClick={() => run(() => api.post(`/work-orders/${wo.id}/compliance/submit-complete`, { completion_notes: notes }), "Submitted for SJ review")}>
              {c.completion_allowed ? <><CheckCircle2 className="w-4 h-4 mr-1" /> Submit for SJ Review</> : <><Lock className="w-4 h-4 mr-1" /> Submit (locked)</>}
            </Button>
            <p className="text-xs text-slate-400">Submitting sends this to SJ for review. It is not marked completed automatically.</p>
          </div>
        </>
      )}

      {submitted && c.stage === "Ready for Review" && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-sm text-purple-800" data-testid="awaiting-review">
          Submitted to SJ for review. You'll be notified once it's approved or if corrections are needed.
        </div>
      )}

      {/* Records */}
      {c.material_requests.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h4 className="font-head font-semibold text-slate-900 text-sm mb-2 flex items-center gap-1"><Package className="w-4 h-4" /> Material Requests</h4>
          <div className="space-y-2">{c.material_requests.map((m) => (
            <div key={m.id} data-testid={`material-${m.id}`} className="border border-slate-200 rounded-lg p-3 text-sm">
              <div className="flex items-center justify-between"><span className="font-medium text-slate-800">{m.item} · {money(m.estimated_cost)}</span><span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${MAT_COLORS[m.status]}`}>{m.status}</span></div>
              {m.vendor && <div className="text-xs text-slate-500">Vendor: {m.vendor}</div>}
              {m.reason && <div className="text-xs text-slate-500">{m.reason}</div>}
              {m.admin_note && <div className="text-xs text-amber-700 mt-0.5">SJ: {m.admin_note}</div>}
              {m.status === "Approved" && !m.receipt_path && <ReceiptUpload mid={m.id} onDone={onChange ? () => { onChange(); load(); } : load} />}
              {m.receipt_path && <a href={fileUrl(m.receipt_path)} target="_blank" rel="noreferrer" className="text-xs text-blue-600 flex items-center gap-1 mt-1"><Receipt className="w-3 h-3" /> View receipt</a>}
            </div>
          ))}</div>
        </div>
      )}
      {c.change_orders.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h4 className="font-head font-semibold text-slate-900 text-sm mb-2 flex items-center gap-1"><FileWarning className="w-4 h-4" /> Change Orders</h4>
          <div className="space-y-2">{c.change_orders.map((o) => (
            <div key={o.id} data-testid={`change-order-${o.id}`} className="border border-slate-200 rounded-lg p-3 text-sm">
              <div className="flex items-center justify-between"><span className="font-medium text-slate-800">{o.description}</span><span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${CO_COLORS[o.status]}`}>{o.status}</span></div>
              <div className="text-xs text-slate-500">+Labor {money(o.additional_labor)} · +Material {money(o.additional_material)}</div>
              {o.admin_note && <div className="text-xs text-amber-700 mt-0.5">SJ: {o.admin_note}</div>}
            </div>
          ))}</div>
        </div>
      )}
      {c.issues.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h4 className="font-head font-semibold text-slate-900 text-sm mb-2 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Reported Issues</h4>
          <div className="space-y-2">{c.issues.map((i) => (
            <div key={i.id} data-testid={`issue-${i.id}`} className="border border-slate-200 rounded-lg p-3 text-sm">
              <div className="flex items-center justify-between"><span className="font-medium text-slate-800">{i.issue_type}</span><span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{i.status}</span></div>
              <div className="text-xs text-slate-500">{i.notes}</div>
            </div>
          ))}</div>
        </div>
      )}
    </div>
  );
}

function FilePick({ onFile, label = "Attach photo/estimate (optional)", testid }) {
  const ref = useRef();
  const [name, setName] = useState("");
  return (
    <div>
      <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" data-testid={testid}
        onChange={(e) => { const f = e.target.files?.[0]; setName(f?.name || ""); onFile(f); }} />
      <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => ref.current?.click()}><Upload className="w-3.5 h-3.5 mr-1" /> {name || label}</Button>
    </div>
  );
}

function MaterialDialog({ wo, threshold, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ item: "", vendor: "", estimated_cost: "", reason: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const cost = parseFloat(f.estimated_cost || 0);
  const submit = async () => {
    if (!f.item.trim()) return toast.error("Item is required");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("item", f.item); fd.append("vendor", f.vendor); fd.append("estimated_cost", cost); fd.append("reason", f.reason);
      if (file) fd.append("file", file);
      await api.post(`/work-orders/${wo.id}/material-requests`, fd);
      toast.success(cost > threshold ? "Sent to SJ for approval" : "Material approved — upload receipt after purchase");
      setOpen(false); setF({ item: "", vendor: "", estimated_cost: "", reason: "" }); setFile(null); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="request-material-btn" variant="outline" className="w-full"><Package className="w-4 h-4 mr-1" /> Request Material Purchase</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head">Request Material Purchase</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Material / item</Label><Input data-testid="mat-item" value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} className="mt-1" /></div>
          <div><Label>Vendor / store</Label><Input data-testid="mat-vendor" value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} className="mt-1" /></div>
          <div><Label>Estimated cost</Label><Input data-testid="mat-cost" type="number" value={f.estimated_cost} onChange={(e) => setF({ ...f, estimated_cost: e.target.value })} className="mt-1" /></div>
          <div><Label>Reason</Label><Textarea data-testid="mat-reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className="mt-1" rows={2} /></div>
          <FilePick testid="mat-file" onFile={setFile} />
          {cost > threshold && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Over ${threshold} — requires SJ admin approval before purchase.</div>}
          <Button data-testid="mat-submit" disabled={busy} onClick={submit} className="w-full bg-blue-600 hover:bg-blue-700">Submit Request</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReceiptUpload({ mid, onDone }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); await api.post(`/material-requests/${mid}/receipt`, fd); toast.success("Receipt uploaded — marked Purchased"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return <div className="mt-2">{busy ? <Loader2 className="w-4 h-4 animate-spin text-blue-600" /> : <FilePick testid={`receipt-${mid}`} label="Upload receipt" onFile={upload} />}</div>;
}

function ChangeOrderDialog({ wo, warning, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ description: "", reason: "", additional_labor: "", additional_material: "", notes: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.description.trim()) return toast.error("Description is required");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("description", f.description); fd.append("reason", f.reason);
      fd.append("additional_labor", parseFloat(f.additional_labor || 0)); fd.append("additional_material", parseFloat(f.additional_material || 0));
      fd.append("notes", f.notes); if (file) fd.append("file", file);
      await api.post(`/work-orders/${wo.id}/change-orders`, fd);
      toast.success("Change order submitted for SJ approval");
      setOpen(false); setF({ description: "", reason: "", additional_labor: "", additional_material: "", notes: "" }); setFile(null); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="request-change-order-btn" variant="outline" className="w-full"><FileWarning className="w-4 h-4 mr-1" /> Request Change Order</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head">Request Change Order</DialogTitle></DialogHeader>
        <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800 font-semibold flex items-start gap-2" data-testid="change-order-warning"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {warning}</div>
        <div className="space-y-3 mt-2">
          <div><Label>Description of additional work</Label><Textarea data-testid="co-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1" rows={2} /></div>
          <div><Label>Why is it needed?</Label><Textarea data-testid="co-reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className="mt-1" rows={2} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Additional labor</Label><Input data-testid="co-labor" type="number" value={f.additional_labor} onChange={(e) => setF({ ...f, additional_labor: e.target.value })} className="mt-1" /></div>
            <div><Label>Additional material</Label><Input data-testid="co-material" type="number" value={f.additional_material} onChange={(e) => setF({ ...f, additional_material: e.target.value })} className="mt-1" /></div>
          </div>
          <div><Label>Contractor notes</Label><Textarea data-testid="co-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1" rows={2} /></div>
          <FilePick testid="co-file" onFile={setFile} />
          <Button data-testid="co-submit" disabled={busy} onClick={submit} className="w-full bg-blue-600 hover:bg-blue-700">Submit Change Order</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function IssueDialog({ wo, types, onDone }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!type) return toast.error("Select an issue type");
    if (!notes.trim()) return toast.error("Notes are required");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("issue_type", type); fd.append("notes", notes); if (file) fd.append("file", file);
      await api.post(`/work-orders/${wo.id}/issues`, fd);
      toast.success("Issue reported to SJ");
      setOpen(false); setType(""); setNotes(""); setFile(null); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="report-issue-btn" variant="outline" className="w-full text-amber-700 border-amber-200"><AlertTriangle className="w-4 h-4 mr-1" /> Report Issue</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head">Report Delay / Job Problem</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Issue type</Label>
            <Select value={type} onValueChange={setType}><SelectTrigger data-testid="issue-type" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Notes</Label><Textarea data-testid="issue-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" rows={3} /></div>
          <FilePick testid="issue-file" onFile={setFile} />
          <Button data-testid="issue-submit" disabled={busy} onClick={submit} className="w-full bg-amber-600 hover:bg-amber-700">Notify SJ</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SignoffUpload({ wo, signoff, onDone }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); await api.post(`/work-orders/${wo.id}/compliance/signoff`, fd); toast.success("Customer sign-off uploaded"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <div className="border-t border-slate-100 pt-2">
      <div className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1"><PenLine className="w-3.5 h-3.5" /> Customer sign-off required</div>
      {signoff?.path ? <a href={fileUrl(signoff.path)} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 flex items-center gap-1"><Check className="w-3 h-3" /> Uploaded — view</a>
        : busy ? <Loader2 className="w-4 h-4 animate-spin text-blue-600" /> : <FilePick testid="signoff-file" label="Upload signed sheet" onFile={upload} />}
    </div>
  );
}
